/**
 * ============================================================================
 * 3D AERODYNAMIC PAPER PHYSICS ENGINE (physics.ts)
 * ============================================================================
 * Simulates a physical sheet of paper pinned at a single top pivot point,
 * reacting to wind wakes, cursor swipes, drag, gravity, lift, and wall collisions.
 *
 * Degrees of Freedom:
 * 1. angleZ: In-plane pendulum swing around the pin (gravity pendulum).
 * 2. angleX: Out-of-plane lift off the plaster wall into 3D perspective space
 *    (aerodynamic lift pulling paper forward, with solid plaster wall bounce).
 * 3. angleY: Torsional twist across the vertical axis when wind hits an edge.
 * 4. flutterEnergy: Travelling wave energy distributed across subsurfaces.
 * ============================================================================
 */

export interface Paper3DState {
  // --- Z Axis: Pendulum swing around top pin ---
  angleZ: number;
  velZ: number;
  targetZ: number;

  // --- X Axis: Lift off the wall into 3D space ---
  angleX: number;
  velX: number;

  // --- Y Axis: Torsional edge twist ---
  angleY: number;
  velY: number;

  // Spring & damping constants
  stiffnessZ: number;
  dampingZ: number;
  stiffnessX: number;
  dampingX: number;
  stiffnessY: number;
  dampingY: number;

  // Flutter energy for subsurfaces
  flutterEnergy: number;

  isSleeping: boolean;
}

export function createPaper3D(initialAngle = 0): Paper3DState {
  return {
    angleZ: initialAngle,
    velZ: 0,
    targetZ: initialAngle,

    angleX: 0,
    velX: 0,

    angleY: 0,
    velY: 0,

    // Tuned for natural, organic paper flutter on a plaster wall
    stiffnessZ: 125,
    dampingZ: 0.93,

    stiffnessX: 165,
    dampingX: 0.88,

    stiffnessY: 190,
    dampingY: 0.86,

    flutterEnergy: 0,
    isSleeping: true
  };
}

/**
 * Applies aerodynamic wind impulse based on cursor/finger swipe velocity and proximity.
 */
export function applyWindImpulse(
  state: Paper3DState,
  vx: number,
  vy: number,
  speed: number,
  distance: number,
  relX: number,
  maxInfluenceRadius = 140
): Paper3DState {
  if (distance > maxInfluenceRadius) return state;

  // Proximity falloff (non-linear pressure gradient)
  const normDist = distance / maxInfluenceRadius;
  const influence = Math.pow(1 - normDist, 1.5);

  // Speed-based energy transfer
  const clampedSpeed = Math.min(Math.max(speed, 40), 3800);
  const speedRatio = clampedSpeed / 1000;

  // 1. Z-torque (pendulum swing):
  const torqueFromVx = (vx / 1000) * 58;
  const torqueFromVy = (vy / 1000) * relX * 32;
  const impulseZ = influence * (torqueFromVx + torqueFromVy);

  // 2. X-lift (lifting off the wall toward viewer):
  const liftForce = speedRatio * 38;
  const impulseX = -influence * liftForce; // negative X rotation lifts bottom forward

  // 3. Y-twist (asymmetrical edge flutter):
  const impulseY = influence * relX * speedRatio * 26;

  // 4. Flutter wave energy:
  const impulseFlutter = influence * speedRatio * 42;

  return {
    ...state,
    velZ: Math.max(-190, Math.min(190, state.velZ + impulseZ)),
    velX: Math.max(-150, Math.min(50, state.velX + impulseX)),
    velY: Math.max(-100, Math.min(100, state.velY + impulseY)),
    flutterEnergy: Math.min(80, state.flutterEnergy + impulseFlutter),
    isSleeping: false
  };
}

/**
 * Direct tap/click impulse (sharp tactile push).
 */
export function applyTouchImpulse(state: Paper3DState, strength = 28): Paper3DState {
  const dir = Math.random() > 0.5 ? 1 : -1;
  return {
    ...state,
    velZ: state.velZ + dir * strength,
    velX: state.velX - (18 + Math.random() * 14),
    velY: state.velY + dir * (10 + Math.random() * 8),
    flutterEnergy: Math.min(60, state.flutterEnergy + strength * 1.1),
    isSleeping: false
  };
}

/**
 * Steps the 3D physics simulation forward by dt seconds.
 */
export function stepPaper3D(
  state: Paper3DState,
  dt: number,
  elapsedTime: number
): Paper3DState {
  if (state.isSleeping) return state;

  const delta = Math.min(dt, 0.035);

  // --- 1. Z-Axis (Pendulum Swing) ---
  const dispZ = state.angleZ - state.targetZ;
  const springZ = -state.stiffnessZ * dispZ;
  const dragZ = -state.dampingZ * state.velZ * 4.2;
  const accZ = springZ + dragZ;
  let newVelZ = state.velZ + accZ * delta;
  let newAngleZ = state.angleZ + newVelZ * delta;

  // --- 2. X-Axis (Lift & Plaster Wall Collision) ---
  const dispX = state.angleX; // Rest position is 0 (flat against wall)
  const springX = -state.stiffnessX * dispX;
  const dragX = -state.dampingX * state.velX * 5.0;
  const accX = springX + dragX;
  let newVelX = state.velX + accX * delta;
  let newAngleX = state.angleX + newVelX * delta;

  // Solid Plaster Wall Constraint:
  // Paper cannot penetrate backwards into the wall (angleX > 0).
  if (newAngleX > 0) {
    newAngleX = 0;
    newVelX = -newVelX * 0.28;
  }

  // --- 3. Y-Axis (Torsional Twist) ---
  const dispY = state.angleY;
  const springY = -state.stiffnessY * dispY;
  const dragY = -state.dampingY * state.velY * 5.8;
  const accY = springY + dragY;
  let newVelY = state.velY + accY * delta;
  let newAngleY = state.angleY + newVelY * delta;

  // --- 4. Flutter wave energy decay ---
  const newFlutterEnergy = state.flutterEnergy * 0.94;

  // Sleep detection: check if all DOFs have come to rest
  const isSettledZ = Math.abs(newAngleZ - state.targetZ) < 0.06 && Math.abs(newVelZ) < 0.12;
  const isSettledX = Math.abs(newAngleX) < 0.1 && Math.abs(newVelX) < 0.18;
  const isSettledY = Math.abs(newAngleY) < 0.08 && Math.abs(newVelY) < 0.18;
  const isSettledFlutter = newFlutterEnergy < 0.2;

  if (isSettledZ && isSettledX && isSettledY && isSettledFlutter) {
    return {
      ...state,
      angleZ: state.targetZ,
      velZ: 0,
      angleX: 0,
      velX: 0,
      angleY: 0,
      velY: 0,
      flutterEnergy: 0,
      isSleeping: true
    };
  }

  return {
    ...state,
    angleZ: newAngleZ,
    velZ: newVelZ,
    angleX: newAngleX,
    velX: newVelX,
    angleY: newAngleY,
    velY: newVelY,
    flutterEnergy: newFlutterEnergy,
    isSleeping: false
  };
}

// ============================================================================
// Backwards Compatibility Shims & Enhanced 2D Pendulum Physics
// ============================================================================
export interface PendulumState {
  angle: number;
  velocity: number;
  targetAngle: number;
  damping: number;
  stiffness: number;
}

export function createPendulum(initialAngle = 0): PendulumState {
  return {
    angle: initialAngle,
    velocity: 0,
    targetAngle: initialAngle,
    damping: 0.970,
    stiffness: 40
  };
}

export function applyImpulse(
  state: PendulumState,
  force: number,
  newDamping?: number
): PendulumState {
  const nextDamping = newDamping !== undefined ? newDamping : state.damping;
  return {
    ...state,
    velocity: Math.max(-480, Math.min(480, state.velocity + force)),
    damping: nextDamping
  };
}

export function stepPendulum(state: PendulumState, dt: number): PendulumState {
  const delta = Math.min(dt, 0.04);
  const displacement = state.angle - state.targetAngle;
  const springForce = -state.stiffness * displacement;

  // Frame-rate independent decay (normalized to 60 FPS)
  const decayFactor = Math.pow(state.damping, delta * 60);
  let newVelocity = (state.velocity + springForce * delta) * decayFactor;
  let newAngle = state.angle + newVelocity * delta;

  // Soft mechanical bounce limit at +/- 76 degrees so paper stays visually anchored by thumbtack
  if (newAngle > 76) {
    newAngle = 76;
    newVelocity = -newVelocity * 0.25;
  } else if (newAngle < -76) {
    newAngle = -76;
    newVelocity = -newVelocity * 0.25;
  }

  const isSettled =
    Math.abs(newAngle - state.targetAngle) < 0.04 && Math.abs(newVelocity) < 0.05;

  return {
    ...state,
    angle: isSettled ? state.targetAngle : newAngle,
    velocity: isSettled ? 0 : newVelocity,
    damping: isSettled ? 0.970 : state.damping
  };
}
